// MonthlyBalanceCards.tsx
import React, { useLayoutEffect, useMemo, useState, ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useDragScrollSnap } from "@/hooks/useDragScrollSnap";
import { copyTextToClipboard } from "@/utils/clipboard";
import {
  balanceTone,
  dateKeyLocal,
  isWeekendDate,
  lastBusinessDayKeys,
  type BalanceTone,
} from "@/utils/balanceLowThreshold";
import { cn } from "@/lib/utils";

interface BalancePoint {
  date: string;
  balance: number;
}

export interface OfferLowBalanceConfig {
  /** Daily-equivalent payment from the offer calc. */
  dailyPayment: number;
  /** Term in days — size of the trailing business-day window. */
  term: number;
  /** Precomputed YYYY-MM-DD keys for the dynamic window (optional; computed if omitted). */
  dynamicDayKeys?: Set<string>;
}

interface MonthlyBalanceCardsProps {
  data: BalancePoint[];
  headerCard?: ReactNode;
  offerLowBalance?: OfferLowBalanceConfig | null;
  /** ISO dates (YYYY-MM-DD) of NSF_RETURN rows for per-month NSF chips. */
  nsfDates?: string[];
}

/** Max calendar days; left col takes 16, right takes 15 (column-major). */
const DAY_SLOTS = 31;
const GRID_ROWS = 16;

const VISIBLE_CARDS = 3;
const CARD_GAP = 12;

const TONE_CLASS: Record<BalanceTone, string> = {
  negative: "text-red-600",
  /** One amber for all low-balance hits (base or dynamic threshold). */
  low: "text-amber-700",
  normal: "text-foreground",
};

const parseYMD = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return { year: y, month: m, day: d };
};

const makeLocalDate = (dateStr: string): Date => {
  const { year, month, day } = parseYMD(dateStr);
  return new Date(year, month - 1, day);
};

function formatBalance(balance: number): string {
  return `$${balance.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function balanceToneClass(
  balance: number,
  offer: OfferLowBalanceConfig | null | undefined,
  dynamicDayKeys: Set<string> | undefined,
  date: Date,
  weekend: boolean
): string {
  const daily = offer?.dailyPayment;
  const dynamicDay =
    daily != null && daily > 0 && dynamicDayKeys != null && dynamicDayKeys.has(dateKeyLocal(date));
  return TONE_CLASS[balanceTone(balance, { dailyPayment: daily, dynamicDay, weekend })];
}

function monthCopyText(label: string, rows: { date: Date; balance: number }[]): string {
  const lines = rows.map(
    (row) =>
      `${row.date.toLocaleDateString("en-US", { month: "short", day: "numeric" })}\t${formatBalance(row.balance)}`
  );
  return `${label}\nDate\tBalance\n${lines.join("\n")}`;
}

type DaySlot =
  | { kind: "pad" }
  | {
      kind: "day";
      day: number;
      date: Date;
      balance: number | null;
      weekend: boolean;
    };

function buildDaySlots(
  year: number,
  month: number,
  rows: { date: Date; balance: number }[]
): DaySlot[] {
  const byDay = new Map(rows.map((r) => [r.date.getDate(), r]));
  const daysInMonth = new Date(year, month, 0).getDate();

  return Array.from({ length: DAY_SLOTS }, (_, i) => {
    const day = i + 1;
    if (day > daysInMonth) return { kind: "pad" as const };

    const row = byDay.get(day);
    const date = row?.date ?? new Date(year, month - 1, day);
    return {
      kind: "day" as const,
      day,
      date,
      balance: row?.balance ?? null,
      weekend: isWeekendDate(date),
    };
  });
}

const MonthCountChip = ({
  count,
  className,
  label,
}: {
  count: number;
  className: string;
  label: string;
}) =>
  count > 0 ? (
    <span
      className={cn(
        "inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[0.6rem] font-semibold leading-none",
        className
      )}
      aria-label={label}
    >
      {count}
    </span>
  ) : null;

const MonthlyBalanceCards: React.FC<MonthlyBalanceCardsProps> = ({
  data,
  headerCard,
  offerLowBalance,
  nsfDates = [],
}) => {
  const monthlyData = useMemo(() => {
    const map = new Map<
      string,
      { label: string; year: number; month: number; rows: { date: Date; balance: number }[] }
    >();

    data.forEach((d) => {
      const dt = makeLocalDate(d.date);
      if (Number.isNaN(dt.getTime())) return;

      const { year, month } = parseYMD(d.date);
      const monthKey = `${year}-${String(month).padStart(2, "0")}`;
      const monthLabel = dt.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });

      const existing = map.get(monthKey);
      if (existing) {
        existing.rows.push({ date: dt, balance: d.balance });
      } else {
        map.set(monthKey, {
          label: monthLabel,
          year,
          month,
          rows: [{ date: dt, balance: d.balance }],
        });
      }
    });

    const MIN_DAYS_PER_MONTH = 2;

    return Array.from(map.entries())
      .filter(([, value]) => value.rows.length >= MIN_DAYS_PER_MONTH)
      .sort(([aKey], [bKey]) => (aKey > bKey ? -1 : 1))
      .map(([key, value]) => {
        const rows = value.rows.sort((a, b) => a.date.getTime() - b.date.getTime());
        return {
          key,
          label: value.label,
          rows,
          slots: buildDaySlots(value.year, value.month, rows),
          negativeDays: rows.filter((r) => r.balance < 0).length,
        };
      });
  }, [data]);

  const [viewportWidth, setViewportWidth] = useState(0);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const cardWidth = useMemo(() => {
    if (viewportWidth <= 0) return 280;
    return (viewportWidth - CARD_GAP * (VISIBLE_CARDS - 1)) / VISIBLE_CARDS;
  }, [viewportWidth]);
  const itemStride = cardWidth + CARD_GAP;

  const { ref, onPointerDown } = useDragScrollSnap<HTMLDivElement>({
    itemStride,
    visibleCount: VISIBLE_CARDS,
  });

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setViewportWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  const dynamicDayKeys = useMemo(() => {
    if (!offerLowBalance || !(offerLowBalance.dailyPayment > 0) || !(offerLowBalance.term > 0)) {
      return undefined;
    }
    return offerLowBalance.dynamicDayKeys ?? lastBusinessDayKeys(data, offerLowBalance.term);
  }, [offerLowBalance, data]);

  const monthsWithCounts = useMemo(() => {
    const nsfByMonth = new Map<string, number>();
    for (const iso of nsfDates) {
      const ym = iso.slice(0, 7);
      if (!/^\d{4}-\d{2}$/.test(ym)) continue;
      nsfByMonth.set(ym, (nsfByMonth.get(ym) ?? 0) + 1);
    }
    return monthlyData.map((month) => {
      let lowDays = 0;
      for (const row of month.rows) {
        const weekend = isWeekendDate(row.date);
        const dynamicDay =
          dynamicDayKeys != null &&
          offerLowBalance != null &&
          offerLowBalance.dailyPayment > 0 &&
          dynamicDayKeys.has(dateKeyLocal(row.date));
        if (
          balanceTone(row.balance, {
            dailyPayment: offerLowBalance?.dailyPayment,
            dynamicDay,
            weekend,
          }) === "low"
        ) {
          lowDays += 1;
        }
      }
      return {
        ...month,
        lowDays,
        nsfCount: nsfByMonth.get(month.key) ?? 0,
      };
    });
  }, [monthlyData, nsfDates, dynamicDayKeys, offerLowBalance]);

  const handleCopy = async (
    key: string,
    label: string,
    rows: { date: Date; balance: number }[]
  ) => {
    try {
      await copyTextToClipboard(monthCopyText(label, rows));
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey((cur) => (cur === key ? null : cur)), 1500);
    } catch {
      /* ignore */
    }
  };

  if (!monthlyData.length && !headerCard) {
    return <p className="mt-2 text-sm text-muted-foreground">No daily balance data available.</p>;
  }

  const slots = [
    ...(headerCard ? [{ key: "header", kind: "header" as const, node: headerCard }] : []),
    ...monthsWithCounts.map((month) => ({ key: month.key, kind: "month" as const, month })),
  ];

  return (
    <div className="mt-4">
      <div
        ref={ref}
        onPointerDown={onPointerDown}
        className={cn(
          // Live scrollLeft + settle on release (useDragScrollSnap); no CSS snap.
          "cursor-grab touch-none overflow-x-auto pb-3 active:cursor-grabbing",
          "select-none [-ms-overflow-style:none] [scrollbar-width:thin]"
        )}
      >
        <div className="flex items-stretch px-0" style={{ gap: CARD_GAP }}>
          {slots.map((slot) => (
            <div
              key={slot.key}
              className="shrink-0"
              style={{ width: cardWidth > 0 ? cardWidth : `${100 / VISIBLE_CARDS}%` }}
            >
              {slot.kind === "header" ? (
                <div className="flex h-full w-full">{slot.node}</div>
              ) : (
                <Card className="flex h-full w-full flex-col shadow-sm">
                  <CardHeader className="space-y-0 p-2.5 pb-1">
                    <div className="flex items-center gap-1.5">
                      <CardTitle className="min-w-0 flex-1 truncate text-xs font-semibold">
                        {slot.month.label}
                      </CardTitle>
                      <MonthCountChip
                        count={slot.month.negativeDays}
                        className="bg-rose-100 text-rose-700 ring-1 ring-rose-200/80"
                        label={`${slot.month.negativeDays} negative days`}
                      />
                      <MonthCountChip
                        count={slot.month.lowDays}
                        className="bg-amber-100 text-amber-800 ring-1 ring-amber-200/80"
                        label={`${slot.month.lowDays} low balance days`}
                      />
                      <MonthCountChip
                        count={slot.month.nsfCount}
                        className="bg-violet-100 text-violet-700 ring-1 ring-violet-200/80"
                        label={`${slot.month.nsfCount} NSF`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label={`Copy ${slot.month.label} balances`}
                        onClick={() =>
                          void handleCopy(slot.month.key, slot.month.label, slot.month.rows)
                        }
                      >
                        {copiedKey === slot.month.key ? (
                          <Check className="h-3.5 w-3.5 text-green-600" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col p-2.5 pt-1">
                    <div
                      className="grid h-[17.5rem] grid-flow-col grid-cols-2 gap-x-1.5"
                      style={{ gridTemplateRows: `repeat(${GRID_ROWS}, minmax(0, 1fr))` }}
                    >
                      {slot.month.slots.map((daySlot, index) => {
                        if (daySlot.kind === "pad") {
                          return <div key={`pad-${index}`} aria-hidden className="min-h-0" />;
                        }

                        return (
                          <div
                            key={daySlot.day}
                            className={cn(
                              "flex min-h-0 items-center justify-between gap-0.5 px-1 text-[0.62rem] leading-none",
                              daySlot.weekend && "rounded-sm bg-muted/70"
                            )}
                          >
                            {daySlot.balance != null ? (
                              <>
                                <span className="shrink-0 text-muted-foreground">
                                  {daySlot.date.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                </span>
                                <span
                                  className={cn(
                                    "min-w-0 truncate text-right tabular-nums",
                                    balanceToneClass(
                                      daySlot.balance,
                                      offerLowBalance,
                                      dynamicDayKeys,
                                      daySlot.date,
                                      daySlot.weekend
                                    )
                                  )}
                                >
                                  {formatBalance(daySlot.balance)}
                                </span>
                              </>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ))}
          <div className="w-1 shrink-0" />
        </div>
      </div>
    </div>
  );
};

export default MonthlyBalanceCards;
